import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  active:         "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  renewal_soon:   "border-yellow-500/40 text-yellow-300 bg-yellow-500/10",
  renewal_urgent: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  expired:        "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

const empty = { policy_type: "General Liability", provider: "", policy_number: "",
                premium: "", renewal_date: new Date().toISOString().slice(0, 10),
                coverage_limit: "", deductible: "", notes: "" };

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function InsurancePage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get("/insurance", { params: { property_id: propertyId } });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      policy_type: i.policy_type || "", provider: i.provider || "",
      policy_number: i.policy_number || "", premium: i.premium || "",
      renewal_date: (i.renewal_date || "").slice(0, 10),
      coverage_limit: i.coverage_limit || "", deductible: i.deductible || "",
      notes: i.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        premium: Number(form.premium || 0),
        coverage_limit: form.coverage_limit ? Number(form.coverage_limit) : undefined,
        deductible: form.deductible ? Number(form.deductible) : undefined,
      };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] == null) && delete payload[k]);
      if (editing) {
        await api.patch(`/insurance/${editing.id}`, payload);
        toast.success("Policy updated");
      } else {
        await api.post("/insurance", { ...payload, property_id: propertyId });
        toast.success("Policy added");
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.policy_type} policy from ${p.provider}?`)) return;
    try {
      await api.delete(`/insurance/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Risk" title="Insurance Tracking" subtitle="Every policy in one place with automatic renewal-urgency badges so you never miss a renewal window." />

      <div className="flex items-center gap-2 mb-3">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="insurance-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add policy
        </Button>
        <div className="ml-auto text-xs text-zinc-500">{items.length} polic{items.length === 1 ? "y" : "ies"}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((i) => (
          <Card key={i.id} className="border-white/10 bg-card p-4" data-testid={`insurance-row-${i.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-orange-400" />
                  <div className="font-medium text-zinc-100">{i.policy_type}</div>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{i.provider} · <span className="font-mono">{i.policy_number}</span></div>
              </div>
              <Badge variant="outline" className={STATUS_TONE[i.status] || STATUS_TONE.active}>
                {(i.status || "active").replace(/_/g, " ")}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="rounded-md border border-white/5 px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Premium</div>
                <div className="tabular-nums text-white">{money(i.premium)}</div>
              </div>
              <div className="rounded-md border border-white/5 px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Coverage</div>
                <div className="tabular-nums text-white">{i.coverage_limit ? money(i.coverage_limit) : "—"}</div>
              </div>
              <div className="rounded-md border border-white/5 px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Deductible</div>
                <div className="tabular-nums text-white">{i.deductible ? money(i.deductible) : "—"}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="text-zinc-500">Renews {new Date(i.renewal_date).toLocaleDateString()}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(i)} className="h-7 border-white/10 text-xs" data-testid={`insurance-edit-${i.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(i)} className="h-7 text-zinc-400 hover:text-red-300" data-testid={`insurance-delete-${i.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-8 text-center border-white/10 md:col-span-2" data-testid="insurance-empty">
            <ShieldCheck className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
            <div className="text-sm text-zinc-400">No policies tracked. Click <b>Add policy</b> to log your first one.</div>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="insurance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-orange-400" /> {editing ? "Edit policy" : "Add policy"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Renewal urgency is calculated automatically from the renewal date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Policy type</Label>
                <Input value={form.policy_type} onChange={(e) => setForm({ ...form, policy_type: e.target.value })}
                       placeholder="General Liability" className="bg-white/[0.04] border-white/10" data-testid="insurance-type" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Provider</Label>
                <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                       placeholder="State Farm" className="bg-white/[0.04] border-white/10" data-testid="insurance-provider" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Policy #</Label>
                <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                       placeholder="POL-123456" className="bg-white/[0.04] border-white/10 font-mono text-xs" data-testid="insurance-number" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Renewal date</Label>
                <Input type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="insurance-renewal" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Premium ($)</Label>
                <Input type="number" value={form.premium} onChange={(e) => setForm({ ...form, premium: e.target.value })}
                       placeholder="12000" className="bg-white/[0.04] border-white/10" data-testid="insurance-premium" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Coverage ($)</Label>
                <Input type="number" value={form.coverage_limit} onChange={(e) => setForm({ ...form, coverage_limit: e.target.value })}
                       placeholder="2000000" className="bg-white/[0.04] border-white/10" data-testid="insurance-coverage" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Deductible ($)</Label>
                <Input type="number" value={form.deductible} onChange={(e) => setForm({ ...form, deductible: e.target.value })}
                       placeholder="5000" className="bg-white/[0.04] border-white/10" data-testid="insurance-deductible" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Broker contact, endorsements, historical claims…"
                        className="bg-white/[0.04] border-white/10 text-sm" data-testid="insurance-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.policy_type || !form.provider}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="insurance-save-btn">
              {busy ? "Saving…" : editing ? "Save changes" : "Add policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
